// Your settings at /settings: account (name, avatar, email, password), appearance (theme, message display), this device's
// notifications, and signing out. Built on the shared SettingsShell, like a server's settings.
import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { GIF_PLAYS, MAX_AVATAR_BYTES, MAX_NAME_LENGTH, THEMES, type ThemeId, type Me, type ChatLayout, type MessageDisplay, type TextSize, type ThemePref, type TimestampFormat } from "@bloom/shared";
import { api, ApiError } from "../api.ts";
import { useSession } from "../session.tsx";
import { useTheme, setThemePref } from "../theme.ts";
import { setMessageDisplay, useMessageDisplay } from "../density.ts";
import { setChatLayout, useChatLayout } from "../layout.ts";
import { setServerBanner, useServerBanner } from "../components/classic/bannerPref.ts";
import { NICK_COLORS } from "../components/irc/ircLogic.ts";
import { setByActivity, setDenseList, setFlatList, setGroupIdle, setShowApps, setShowCollections, useByActivity, useDenseList, useFlatList, useGroupIdle, useShowApps, useShowCollections } from "../components/classic/listOptions.ts";
import { setColoredNames, useColoredNames } from "../coloredNames.ts";
import { setShortLinks, useShortLinks } from "../shortLinks.ts";
import { setChannelActivity, useChannelActivity } from "../channelActivity.ts";
import { setReactionEffects, useReactionEffects } from "../reactionEffects.ts";
import { setGifPlays, useGifPlays } from "../gifPlays.ts";
import { setStripTracking, useStripTracking } from "../stripTracking.ts";
import { Flame } from "../components/Flame.tsx";
import { setTextSize, useTextSize } from "../textSize.ts";
import { setTimestampFormat, useTimestampFormat } from "../timestampFormat.ts";
import { formatMessageTime } from "../components/MessageTime.tsx";
import { SettingRow, SettingsShell } from "../components/SettingsShell.tsx";
import { Avatar } from "../components/ui.tsx";
import { ImageCropper } from "../components/ImageCropper.tsx";
import { DevicePush } from "../components/NotifyBell.tsx";
import { registerPush } from "../push.ts";
import { IdlePushSetting } from "../components/IdlePushSetting.tsx";
import { LinkPreviewsSetting } from "../components/LinkPreviewsSetting.tsx";
import { ConnectedApps } from "../components/ConnectedApps.tsx";
import { NameColorSetting } from "../components/NameColorSetting.tsx";
import "../components/notify.css";
import { BUILD, buildCommit, builtAt, buildSummary } from "../buildInfo.ts";

/** System first, then every theme (shared THEMES). */
const THEME_OPTIONS: { id: ThemePref; label: string; blurb: string }[] = [
  { id: "system", label: "System", blurb: "Follows your device between Light and Dark." },
  ...THEMES.map((t) => ({ id: t.id, label: t.label, blurb: t.blurb })),
];
const themeLabel = (p: ThemePref) => THEME_OPTIONS.find((t) => t.id === p)?.label ?? "System";

export function Settings() {
  const initial = new URLSearchParams(useLocation().search).get("section") ?? undefined;
  const { me, refresh, signOut } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const { pref } = useTheme();
  if (me === undefined) return null;
  if (me === null) return <Navigate to="/login?next=/settings" replace />;
  // Back / Esc returns to where settings were opened from (the chat passes its path along).
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  return (
    <SettingsShell
      title="Your settings"
      backTo={from}
      initial={initial}
      sections={[
        { id: "account", label: "My account", hint: me.name, render: () => <Account me={me} refresh={refresh} /> },
        { id: "appearance", label: "Appearance", hint: themeLabel(pref), render: () => <Appearance /> },
        { id: "notifications", label: "Notifications", render: () => <Notifications /> },
        { id: "connected", label: "Connected apps & AI", render: () => <ConnectedApps /> },
        { id: "about", label: "About", hint: BUILD.commit, render: () => <About /> },
        {
          id: "signout",
          label: "Sign out",
          danger: true,
          render: () => (
            <SettingRow label="Sign out of Bloom" help={`You're signed in as ${me.email}. Other devices stay signed in.`}>
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  // Through the session, which also wipes this account's messages cached on the device.
                  await signOut();
                  navigate("/login", { replace: true });
                }}
              >
                Sign out
              </button>
            </SettingRow>
          ),
        },
      ]}
    />
  );
}

// ---- My account ----

function Account({ me, refresh }: { me: Me; refresh: () => Promise<Me | null> }) {
  return (
    <>
      <AvatarSetting me={me} refresh={refresh} />
      <NameSetting me={me} refresh={refresh} />
      <NameColorSetting me={me} refresh={refresh} />
      <SettingRow label="Email" help={me.emailVerified ? "Confirmed." : "Not confirmed yet: check your inbox for the link."}>
        <span className="us-email">{me.email}</span>
      </SettingRow>
      <PasswordSetting />
    </>
  );
}

function NameSetting({ me, refresh }: { me: Me; refresh: () => Promise<Me | null> }) {
  const [name, setName] = useState(me.name);
  const [state, setState] = useState<{ busy?: boolean; error?: string; saved?: boolean }>({});
  const clean = name.trim().replace(/\s+/g, " ");
  const changed = clean !== me.name;
  return (
    <SettingRow label="Display name" help="How you appear in chat, mentions and notifications, in every server.">
      <form
        className="us-inline-form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!changed || !clean) return;
          setState({ busy: true });
          try {
            await api("/me", { method: "PATCH", body: { name: clean } });
            await refresh();
            setState({ saved: true });
          } catch (err) {
            setState({ error: err instanceof Error ? err.message : "Couldn't save." });
          }
        }}
      >
        <label htmlFor="us-name" className="sr-only">Display name</label>
        <input
          id="us-name"
          className="input"
          value={name}
          maxLength={MAX_NAME_LENGTH}
          autoComplete="nickname"
          onChange={(e) => {
            setName(e.target.value);
            setState({});
          }}
        />
        <button className="btn btn-primary" disabled={!changed || !clean || state.busy}>
          {state.busy ? "Saving…" : "Save"}
        </button>
        {state.error && <div className="error us-note" role="alert">{state.error}</div>}
        {state.saved && !changed && <div className="muted us-note" role="status">Saved.</div>}
      </form>
    </SettingRow>
  );
}

function AvatarSetting({ me, refresh }: { me: Me; refresh: () => Promise<Me | null> }) {
  const input = useRef<HTMLInputElement>(null);
  const [cropping, setCropping] = useState<File | null>(null);
  const [state, setState] = useState<{ busy?: boolean; error?: string }>({});
  /** The checkbox shows a change at once, while the server fetches or removes the copy. */
  const [gravatarPending, setGravatarPending] = useState<boolean | null>(null);

  const upload = async (blob: Blob) => {
    setState({ busy: true });
    try {
      const res = await fetch("/api/me/avatar", { method: "PUT", body: blob, headers: { "content-type": blob.type }, credentials: "same-origin" });
      if (!res.ok) throw new ApiError(res.status, ((await res.json().catch(() => null)) as { message?: string } | null)?.message ?? "Upload failed.");
      await refresh();
      setState({});
    } catch (err) {
      setState({ error: err instanceof Error ? err.message : "Upload failed." });
    }
  };

  return (
    <SettingRow
      label="Avatar"
      help={
        me.gravatar
          ? `Your Gravatar for ${me.email}. Bloom keeps a copy and checks gravatar.com for a new picture once a day; nobody else sees your email.`
          : "A square picture, or your Gravatar. Everyone who shares a server with you sees it."
      }
    >
      <div className="us-avatar">
        <Avatar id={me.id} name={me.name} size={56} image={me.image} />
        <div className="us-avatar-actions">
          <button type="button" className="btn btn-small" disabled={state.busy} onClick={() => input.current?.click()}>
            {state.busy ? "Saving…" : me.image ? "Change picture" : "Upload picture"}
          </button>
          <label className="us-check">
            <input
              type="checkbox"
              checked={gravatarPending ?? !!me.gravatar}
              disabled={state.busy}
              onChange={async (e) => {
                const on = e.target.checked;
                setGravatarPending(on);
                setState({ busy: true });
                try {
                  // Off removes the copy, like Remove; uploading a picture also turns it off.
                  await api(on ? "/me/avatar/gravatar" : "/me/avatar", { method: on ? "POST" : "DELETE" });
                  await refresh();
                  setState({});
                } catch (err) {
                  setState({ error: err instanceof Error ? err.message : "Gravatar didn't answer. Try again in a minute." });
                } finally {
                  setGravatarPending(null);
                }
              }}
            />
            Use Gravatar
          </label>
          {me.image && !me.gravatar && (
            <button
              type="button"
              className="link-btn us-quiet"
              disabled={state.busy}
              onClick={async () => {
                setState({ busy: true });
                await api("/me/avatar", { method: "DELETE" }).catch(() => {});
                await refresh();
                setState({});
              }}
            >
              Remove
            </button>
          )}
        </div>
        <input
          ref={input}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = ""; // choosing the same file again still fires
            if (f) setCropping(f);
          }}
        />
      </div>
      {state.error && <div className="error us-note" role="alert">{state.error}</div>}
      {cropping && (
        <ImageCropper
          file={cropping}
          title="Crop your picture"
          view={{ width: 240, height: 240 }}
          out={{ width: 256, height: 256 }}
          maxBytes={MAX_AVATAR_BYTES}
          round
          onCancel={() => setCropping(null)}
          onDone={(blob) => {
            setCropping(null);
            void upload(blob);
          }}
        />
      )}
    </SettingRow>
  );
}

function PasswordSetting() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [others, setOthers] = useState(true);
  const [state, setState] = useState<{ busy?: boolean; error?: string; done?: boolean }>({});
  if (!open) {
    return (
      <SettingRow label="Password" help={state.done ? "Password changed." : "Change the password you sign in with."}>
        <button type="button" className="btn btn-small" onClick={() => setOpen(true)}>Change password</button>
      </SettingRow>
    );
  }
  return (
    <div className="setting-row us-password">
      <form
        className="us-password-form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (next.length < 8) return setState({ error: "Use at least 8 characters for the new password." });
          setState({ busy: true });
          try {
            await api("/auth/change-password", { body: { currentPassword: current, newPassword: next, revokeOtherSessions: others } });
            // Signing out other devices replaces this session too, and push subscriptions follow the session (#230).
            if (others) void registerPush().catch(() => {});
            setCurrent("");
            setNext("");
            setOpen(false);
            setState({ done: true });
          } catch (err) {
            // Better Auth says "Invalid password" for a wrong current password; say which one.
            const msg = err instanceof Error ? err.message : "Couldn't change it.";
            setState({ error: /invalid password/i.test(msg) ? "Your current password isn't right." : msg });
          }
        }}
      >
        <div className="setting-label">Change password</div>
        <input type="text" name="username" autoComplete="username" hidden readOnly />
        <div className="field">
          <label htmlFor="us-current">Current password</label>
          <input id="us-current" className="input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoFocus />
        </div>
        <div className="field">
          <label htmlFor="us-new">New password</label>
          <input id="us-new" className="input" type="password" autoComplete="new-password" minLength={8} value={next} onChange={(e) => setNext(e.target.value)} required />
        </div>
        <label className="us-check">
          <input type="checkbox" checked={others} onChange={(e) => setOthers(e.target.checked)} />
          Sign out my other devices
        </label>
        {state.error && <div className="error" role="alert">{state.error}</div>}
        <div className="row">
          <button className="btn btn-primary" disabled={state.busy}>{state.busy ? "Changing…" : "Change password"}</button>
          <button type="button" className="btn" onClick={() => { setOpen(false); setState({}); }}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

// ---- Appearance ----

const DISPLAYS: { id: MessageDisplay; label: string; help: string }[] = [
  { id: "cozy", label: "Cozy", help: "Profile pictures, with names above messages." },
  { id: "compact", label: "Compact", help: "No profile pictures; time and name on the same line as the message." },
  { id: "bubbles", label: "Bubbles", help: "Messages in bubbles, yours on the right and everyone else's on the left." },
];

/** The layouts (#141, #154), with how each looks in a word. */
const LAYOUTS: { id: ChatLayout; label: string; help: string }[] = [
  { id: "chats", label: "Chats", help: "One list of every chat in every server, newest first." },
  { id: "classic", label: "Classic", help: "The open server's channels by category, with unread pills and the server banner." },
  { id: "irc", label: "IRC", help: "A retro layout after mIRC: a black pane with one monospace line per message, a bar of windows, and a nick list. No pictures." },
];

const TEXT_SIZE_OPTIONS: { id: TextSize; label: string }[] = [
  { id: "small", label: "Small" },
  { id: "medium", label: "Medium" },
  { id: "large", label: "Large" },
];

const TIMESTAMP_OPTIONS: { id: TimestampFormat; label: string; help: string }[] = [
  { id: "default", label: "Default", help: "9:05 AM, 6:41 PM." },
  { id: "relative", label: "Relative", help: "Just now, 12 minutes ago, 49 hours ago, 3 days ago." },
  { id: "exact", label: "Exact", help: "0:12 ago, 3:05 ago, 6d 12:33 ago." },
];

/** Each choice's sample: a message from 6 days, 12 hours and 33 minutes ago. */
const SAMPLE_AGE = ((6 * 24 + 12) * 60 + 33) * 60_000;

/** A tiny Bloom drawn in one theme's own variables (`.theme-<id>`, styles.css). */
function MiniTheme({ id, half }: { id: ThemeId; half?: boolean }) {
  return (
    <span className={`us-mini theme-${id}${half ? " half" : ""}`}>
      <span className="us-mini-rail" />
      <span className="us-mini-side" />
      <span className="us-mini-main"><span /><span /><span /></span>
    </span>
  );
}

/**
 * The theme swatches: a radio group (one tab stop on the chosen one; arrow keys, Home and End move and choose, like
 * native radios).
 */
function ThemePicker({ pref }: { pref: ThemePref }) {
  const group = useRef<HTMLDivElement>(null);
  const choose = (i: number) => {
    const n = THEME_OPTIONS.length;
    const k = (i + n) % n;
    setThemePref(THEME_OPTIONS[k]!.id);
    group.current?.querySelectorAll<HTMLButtonElement>("[role=radio]")[k]?.focus();
  };
  const onKeyDown = (e: ReactKeyboardEvent) => {
    const i = THEME_OPTIONS.findIndex((t) => t.id === pref);
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (step) choose(i + step);
    else if (e.key === "Home") choose(0);
    else if (e.key === "End") choose(THEME_OPTIONS.length - 1);
    else return;
    e.preventDefault();
  };
  return (
    <div ref={group} className="us-themes us-theme-grid" role="radiogroup" aria-label="Theme" onKeyDown={onKeyDown}>
      {THEME_OPTIONS.map((t) => (
        <button
          key={t.id}
          type="button"
          role="radio"
          aria-checked={pref === t.id}
          tabIndex={pref === t.id ? 0 : -1}
          title={t.blurb}
          data-theme-option={t.id}
          className={`us-theme${pref === t.id ? " on" : ""}`}
          onClick={() => setThemePref(t.id)}
        >
          <span className="us-swatch" aria-hidden="true">
            {t.id === "system" ? (
              <>
                <MiniTheme id="light" />
                <MiniTheme id="dark" half />
              </>
            ) : (
              <MiniTheme id={t.id} />
            )}
          </span>
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** A tiny drawing of a layout (#556), from shapes and theme colors, in the theme picker's card: the card's button names it. */
function LayoutWireframe({ id }: { id: ChatLayout }) {
  // The Classic card follows Flat and Dense (#297), as iOS and Android do: no header when flat, more and tighter rows when dense (it runs off the card).
  const flat = useFlatList();
  const dense = useDenseList();
  if (id === "chats") {
    // Rows of a circle, a name bar and a shorter preview bar; the first one is unread.
    return (
      <span className="us-swatch us-ly us-ly-chats" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span key={i} className={`us-ly-chat${i === 0 ? " unread" : ""}`}>
            <i className="pfp" />
            <span className="lines"><i /><i /></span>
          </span>
        ))}
      </span>
    );
  }
  if (id === "classic") {
    // A section header (none when flat), then a "#" and a name bar per row, one row open.
    return (
      <span className={`us-swatch us-ly us-ly-classic${dense ? " dense" : ""}${flat ? " flat" : ""}`} aria-hidden="true">
        {!flat && <i className="head" />}
        {Array.from({ length: (flat ? 1 : 0) + (dense ? 6 : 4) }, (_, i) => i).map((i) => (
          <span key={i} className={`us-ly-row${i === 1 ? " open" : ""}`}>
            <i className="hash" />
            <i className="bar" />
          </span>
        ))}
      </span>
    );
  }
  // IRC: a black pane, a time, a nick bar in a color and a text bar per line.
  return (
    <span className="us-swatch us-ly us-ly-irc" aria-hidden="true">
      {NICK_COLORS.slice(0, 4).map((c, i) => (
        <span key={i} className="us-ly-line">
          <i className="time" />
          <i className="nick" style={{ background: c }} />
          <i className="text" style={{ width: `${[52, 38, 60, 30][i]}%` }} />
        </span>
      ))}
    </span>
  );
}

/** The Layout picker (#556): a radio group like the Theme picker's, arrow keys move the choice and only the chosen card is a tab stop. */
function LayoutPicker({ layout }: { layout: ChatLayout }) {
  const group = useRef<HTMLDivElement>(null);
  const choose = (i: number) => {
    const n = LAYOUTS.length;
    const k = (i + n) % n;
    setChatLayout(LAYOUTS[k]!.id);
    group.current?.querySelectorAll<HTMLButtonElement>("[role=radio]")[k]?.focus();
  };
  const onKeyDown = (e: ReactKeyboardEvent) => {
    const i = LAYOUTS.findIndex((l) => l.id === layout);
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (step) choose(i + step);
    else if (e.key === "Home") choose(0);
    else if (e.key === "End") choose(LAYOUTS.length - 1);
    else return;
    e.preventDefault();
  };
  return (
    <div ref={group} className="us-themes" role="radiogroup" aria-label="Layout" onKeyDown={onKeyDown}>
      {LAYOUTS.map((l) => (
        <button key={l.id} type="button" role="radio" aria-checked={layout === l.id} aria-label={`${l.label} layout`} tabIndex={layout === l.id ? 0 : -1} className={`us-theme${layout === l.id ? " on" : ""}`} onClick={() => setChatLayout(l.id)}>
          <LayoutWireframe id={l.id} />
          {l.label}
        </button>
      ))}
    </div>
  );
}

function Appearance() {
  const { pref, resolved } = useTheme();
  const display = useMessageDisplay();
  const layout = useChatLayout();
  const showBanner = useServerBanner();
  const showApps = useShowApps();
  const showCollections = useShowCollections();
  const flatList = useFlatList();
  const byActivity = useByActivity();
  const denseList = useDenseList();
  const groupIdle = useGroupIdle();
  const textSize = useTextSize();
  const coloredNames = useColoredNames();
  const shortLinks = useShortLinks();
  const timestamps = useTimestampFormat();
  const [sampleNow] = useState(() => Date.now());
  const activity = useChannelActivity();
  const effects = useReactionEffects();
  const stripTracking = useStripTracking();
  const gifPlays = useGifPlays();
  return (
    <>
    <SettingRow
      label="Theme"
      wide
      help={`${pref === "system" ? `Follows your device (${themeLabel(resolved)} right now).` : THEME_OPTIONS.find((t) => t.id === pref)?.blurb ?? ""} Saved to your account, so it follows you to other devices.`}
    >
      <ThemePicker pref={pref} />
    </SettingRow>
    <SettingRow label="Layout" help={`${LAYOUTS.find((l) => l.id === layout)!.help} Remembered on this device only.`}>
      <LayoutPicker layout={layout} />
    </SettingRow>
    <SettingRow label="Message display" help={`${layout === "irc" ? "The IRC layout is always one line per message." : DISPLAYS.find((d) => d.id === display)!.help} Saved to your account.`}>
      <div className="us-themes" role="radiogroup" aria-label="Message display">
        {DISPLAYS.map((d) => (
          <button key={d.id} type="button" role="radio" aria-checked={display === d.id} className={`us-theme${display === d.id ? " on" : ""}`} onClick={() => setMessageDisplay(d.id)}>
            <span className={`us-swatch us-density us-density-${d.id}`} aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <span key={i} className="us-density-row">
                  <span className="us-density-pfp" />
                  <span className="us-density-lines"><span /><span /></span>
                </span>
              ))}
            </span>
            {d.label}
          </button>
        ))}
      </div>
    </SettingRow>
    <SettingRow label="Text size" help="How big messages and the message box are drawn. Saved to your account.">
      <div className="us-themes" role="radiogroup" aria-label="Text size">
        {TEXT_SIZE_OPTIONS.map((t) => (
          <button key={t.id} type="button" role="radio" aria-checked={textSize === t.id} className={`us-theme${textSize === t.id ? " on" : ""}`} onClick={() => setTextSize(t.id)}>
            <span className={`us-swatch us-textsize us-textsize-${t.id}`} aria-hidden="true">Aa</span>
            {t.label}
          </button>
        ))}
      </div>
    </SettingRow>
    <SettingRow label="Colorized names" help="Each person's name, their @mentions and the typing line wear a color of their own, in the order people joined the server. Off draws every name in the plain text color. The IRC layout keeps its own nick colors. Remembered on this device only.">
      <label className="us-switch">
        <input type="checkbox" className="us-check" checked={coloredNames} onChange={(e) => setColoredNames(e.target.checked)} />
        <span>Color people's names</span>
      </label>
    </SettingRow>
    <SettingRow label="Shorten long links" help="A long link shows up to its “?”, then ✂️. Long-press it to show the full link. Opening and copying always use the full link. Remembered on this device only.">
      <label className="us-switch">
        <input type="checkbox" className="us-check" checked={shortLinks} onChange={(e) => setShortLinks(e.target.checked)} />
        <span>Show a long link short</span>
      </label>
    </SettingRow>
    <SettingRow label="Timestamps" help={`${TIMESTAMP_OPTIONS.find((t) => t.id === timestamps)!.help} Saved to your account.`}>
      <div className="us-themes" role="radiogroup" aria-label="Timestamps">
        {TIMESTAMP_OPTIONS.map((t) => (
          <button key={t.id} type="button" role="radio" aria-checked={timestamps === t.id} className={`us-theme${timestamps === t.id ? " on" : ""}`} onClick={() => setTimestampFormat(t.id)}>
            <span className="us-swatch us-stamp" aria-hidden="true">
              {formatMessageTime(sampleNow - SAMPLE_AGE, sampleNow, t.id, false)}
            </span>
            {t.label}
          </button>
        ))}
      </div>
    </SettingRow>
    <SettingRow label="GIF loops" help="How many times a GIF in a message plays before it stops. Click a stopped GIF to play it again. Opened large, a GIF keeps looping. Remembered on this device only.">
      <div className="us-themes" role="radiogroup" aria-label="GIF loops">
        {GIF_PLAYS.map((g) => (
          <button key={g.id} type="button" role="radio" aria-checked={gifPlays === g.id} className={`us-theme${gifPlays === g.id ? " on" : ""}`} onClick={() => setGifPlays(g.id)}>
            {g.label}
          </button>
        ))}
      </div>
    </SettingRow>
    {layout === "classic" && (
    <SettingRow label="Server banner" help="The server's picture over the channel list. Remembered on this device only.">
      <label className="us-switch">
        <input type="checkbox" className="us-check" checked={showBanner} onChange={(e) => setServerBanner(e.target.checked)} />
        <span>Show the server banner</span>
      </label>
    </SettingRow>
    )}
    {layout === "classic" && (
    <SettingRow label="Apps and Collections" help="Off, the section leaves the channel list and the server menu (the server name at the top) carries it instead. Remembered on this device only.">
      <label className="us-switch">
        <input type="checkbox" className="us-check" checked={showApps} onChange={(e) => setShowApps(e.target.checked)} />
        <span>Show Apps in the channel list</span>
      </label>
      <label className="us-switch">
        <input type="checkbox" className="us-check" checked={showCollections} onChange={(e) => setShowCollections(e.target.checked)} />
        <span>Show Collections in the channel list</span>
      </label>
    </SettingRow>
    )}
    {layout === "classic" && (
    <SettingRow label="Channel list" help="Flat shows every channel in one list with no category headers, A to Z. Sort by activity puts the channel with the newest message first. Dense makes the rows shorter. Remembered on this device only.">
      <label className="us-switch">
        <input type="checkbox" className="us-check" checked={flatList} onChange={(e) => setFlatList(e.target.checked)} />
        <span>Flat channel list</span>
      </label>
      {flatList && (
        <label className="us-switch">
          <input type="checkbox" className="us-check" checked={byActivity} onChange={(e) => setByActivity(e.target.checked)} />
          <span>Sort by activity</span>
        </label>
      )}
      <label className="us-switch">
        <input type="checkbox" className="us-check" checked={denseList} onChange={(e) => setDenseList(e.target.checked)} />
        <span>Dense channel list</span>
      </label>
      <label className="us-switch">
        <input type="checkbox" className="us-check" checked={groupIdle} onChange={(e) => setGroupIdle(e.target.checked)} />
        <span>Group idle channels</span>
      </label>
    </SettingRow>
    )}
    {layout === "classic" && (
    <SettingRow
      label="Channel activity"
      help="A flame instead of the unread dot on your busiest channels: the top 10% by new messages since you last read them (at least 10). Saved to your account."
    >
      <label className="us-switch">
        <input type="checkbox" className="us-check" checked={activity} onChange={(e) => setChannelActivity(e.target.checked)} />
        <span>Show a flame on the busiest channels</span>
        <Flame />
      </label>
    </SettingRow>
    )}
    <SettingRow
      label="Reaction effects"
      help="Super reactions zoom, spin or poof in, then glow for a moment. Off plays no animations or sounds for reactions and shows them all as plain reactions. Your system's Reduce motion keeps the sounds and drops the movement. Saved to your account."
    >
      <label className="us-switch">
        <input type="checkbox" className="us-check" checked={effects} onChange={(e) => setReactionEffects(e.target.checked)} />
        <span>Play super reaction effects</span>
      </label>
    </SettingRow>
    <SettingRow
      label="Tracking IDs in links"
      help="Links you send lose the parameters that tell a site who shared them (for example X's ?s= and ?a=, YouTube's si, and utm_*). A YouTube start time stays. Saved to your account."
    >
      <label className="us-switch">
        <input type="checkbox" className="us-check" checked={stripTracking} onChange={(e) => setStripTracking(e.target.checked)} />
        <span>Strip known tracking IDs from social links</span>
      </label>
    </SettingRow>
    <LinkPreviewsSetting />
    </>
  );
}

// ---- About ----

/** The build this page is running, with a button that copies it for a bug report (like StationCast's About card). */
function About() {
  const [copied, setCopied] = useState<"yes" | "no" | null>(null);
  return (
    <div className="us-about">
      <div className="us-about-head">
        <h3>Build</h3>
        <button
          type="button"
          className="us-about-copy"
          aria-label="Copy build info"
          title="Copy build info"
          onClick={async () => {
            const ok = await navigator.clipboard?.writeText(buildSummary()).then(() => true, () => false);
            setCopied(ok ? "yes" : "no");
            setTimeout(() => setCopied(null), 1500);
          }}
        >
          {copied === "yes" ? "Copied" : copied === "no" ? "Couldn't copy" : <CopyGlyph />}
        </button>
      </div>
      <dl>
        <div>
          <dt>App</dt>
          <dd>Bloom web</dd>
        </div>
        <div>
          <dt>Commit</dt>
          <dd className="us-about-mono">{buildCommit}</dd>
        </div>
        <div>
          <dt>Built</dt>
          <dd>{builtAt}</dd>
        </div>
      </dl>
    </div>
  );
}

function CopyGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true">
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
      <path d="M10.5 3.5v-.5a1.5 1.5 0 0 0-1.5-1.5H4A1.5 1.5 0 0 0 2.5 3v5A1.5 1.5 0 0 0 4 9.5h.5" />
    </svg>
  );
}

// ---- Notifications ----

function Notifications() {
  return (
    <>
      <div className="setting-row us-push">
        <div className="setting-text">
          <div className="setting-label">Push notifications on this device</div>
          <div className="setting-help">Each device is turned on separately. Choose how loudly each channel or DM notifies you from the bell in its header.</div>
          <DevicePush />
        </div>
      </div>
      <IdlePushSetting />
    </>
  );
}
